import React, { useState, useEffect } from 'react';
import { 
  DetailedReport, 
  ScoreWithDetails, 
  StrengthItem, 
  WeaknessItem,
  FeedbackItem,
  FeedbackExample,
  ImprovementExample,
  ExampleWriting
} from '../types/models';
import '../styles/AssessmentReport.css';

interface AssessmentReportProps {
  assessmentId: string;
  studentProfileId: string;
  yearLevel: number;
  onClose?: () => void;
  onBack?: () => void;
}

/**
 * Component for displaying assessment reports with scores, feedback, and improvement suggestions.
 * Implements Requirements 4.2, 4.4, 4.5, 5.3, 5.4
 */
export const AssessmentReport: React.FC<AssessmentReportProps> = ({
  assessmentId,
  studentProfileId,
  yearLevel,
  onClose,
  onBack,
}) => {
  const [report, setReport] = useState<DetailedReport | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'overview' | 'details' | 'improvement' | 'example'>('overview');
  const [exampleWritings, setExampleWritings] = useState<ExampleWriting[]>([]);
  const [exampleLoading, setExampleLoading] = useState<boolean>(false);
  const [selectedQuality, setSelectedQuality] = useState<'high' | 'medium' | 'low'>('high');

  useEffect(() => {
    fetchReport();
  }, [assessmentId]);

  const fetchReport = async () => {
    setLoading(true);
    setError('');

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      
      // Get token from localStorage
      const storedTokens = localStorage.getItem('authTokens');
      const token = storedTokens ? JSON.parse(storedTokens).idToken : null;
      
      // First try to get existing report
      let response = await fetch(`${apiUrl}/api/reports/assessment/${assessmentId}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      if (response.status === 404) {
        // Generate new report if not found
        response = await fetch(`${apiUrl}/api/reports`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
          body: JSON.stringify({
            assessmentId,
            studentProfileId,
            yearLevel,
          }),
        });
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to load report');
      }

      setReport(data.report);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const fetchExampleWritings = async (promptId: string) => {
    setExampleLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const storedTokens = localStorage.getItem('authTokens');
      const token = storedTokens ? JSON.parse(storedTokens).idToken : null;
      
      const response = await fetch(
        `${apiUrl}/api/examples?yearLevel=${yearLevel}&promptId=${promptId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setExampleWritings(data.examples || []);
      }
    } catch (err) {
      console.error('Error fetching example writings:', err);
    } finally {
      setExampleLoading(false);
    }
  };

  // Fetch example writings when switching to example tab
  useEffect(() => {
    if (activeTab === 'example' && report && exampleWritings.length === 0) {
      // Use promptId from report if available, otherwise fetch from submission
      if (report.promptId) {
        fetchExampleWritings(report.promptId);
      } else if (report.submissionId) {
        fetchExampleWritingsForReport();
      }
    }
  }, [activeTab, report]);

  const fetchExampleWritingsForReport = async () => {
    if (!report) return;
    
    setExampleLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const storedTokens = localStorage.getItem('authTokens');
      const token = storedTokens ? JSON.parse(storedTokens).idToken : null;
      
      // First get the submission to find the promptId
      const submissionResponse = await fetch(
        `${apiUrl}/api/submissions/status?submissionId=${report.submissionId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
        }
      );
      
      if (submissionResponse.ok) {
        const submissionData = await submissionResponse.json();
        // promptId is returned directly in the response, not nested under submission
        const promptId = submissionData.promptId;
        
        if (promptId) {
          await fetchExampleWritings(promptId);
        }
      }
    } catch (err) {
      console.error('Error fetching example writings for report:', err);
    } finally {
      setExampleLoading(false);
    }
  };

  const getScoreLevelClass = (level: string): string => {
    const levelClasses: Record<string, string> = {
      excellent: 'score-level--excellent',
      good: 'score-level--good',
      satisfactory: 'score-level--satisfactory',
      developing: 'score-level--developing',
      needs_improvement: 'score-level--needs-improvement',
    };
    return levelClasses[level] || '';
  };

  const getScoreColor = (score: number): string => {
    if (score >= 9) return '#4caf50';
    if (score >= 7) return '#8bc34a';
    if (score >= 5) return '#ffc107';
    if (score >= 3) return '#ff9800';
    return '#f44336';
  };

  const renderScoreBar = (score: number) => {
    const percentage = (score / 10) * 100;
    return (
      <div className="score-bar">
        <div 
          className="score-bar__fill" 
          style={{ 
            width: `${percentage}%`,
            backgroundColor: getScoreColor(score)
          }}
        />
        <span className="score-bar__value">{score}/10</span>
      </div>
    );
  };

  const renderOverviewTab = () => {
    if (!report) return null;

    return (
      <div className="report-tab report-tab--overview">
        {/* Summary Section */}
        <div className="report-section report-section--summary">
          <h3 className="report-section__title">Summary</h3>
          <p className="report-summary__text">{report.summary}</p>
        </div>

        {/* Total Score */}
        <div className="report-section report-section--total-score">
          <div className="total-score">
            <div className="total-score__circle">
              <span className="total-score__value">{report.totalScore}</span>
              <span className="total-score__max">/10</span>
            </div>
            <span className="total-score__label">Overall Score</span>
          </div>
        </div>

        {/* Scores Overview */}
        <div className="report-section report-section--scores">
          <h3 className="report-section__title">Scores by Criterion</h3>
          <div className="scores-grid">
            {report.scores.map((score: ScoreWithDetails) => (
              <div key={score.criterion} className="score-item">
                <div className="score-item__header">
                  <span className="score-item__name">{score.displayName}</span>
                  <span className={`score-item__level ${getScoreLevelClass(score.level)}`}>
                    {score.level.replace('_', ' ')}
                  </span>
                </div>
                {renderScoreBar(score.value)}
              </div>
            ))}
          </div>
        </div>

        {/* Strengths & Weaknesses Quick View */}
        <div className="report-section report-section--highlights">
          <div className="highlights-grid">
            <div className="highlight-card highlight-card--strengths">
              <h4 className="highlight-card__title">
                <span className="highlight-icon">✓</span> Strengths
              </h4>
              <ul className="highlight-list">
                {report.strengths.slice(0, 3).map((strength: StrengthItem, index: number) => (
                  <li key={index} className="highlight-list__item">
                    {strength.displayName}
                  </li>
                ))}
              </ul>
            </div>
            <div className="highlight-card highlight-card--improvements">
              <h4 className="highlight-card__title">
                <span className="highlight-icon">↑</span> Areas to Improve
              </h4>
              <ul className="highlight-list">
                {report.areasForImprovement.slice(0, 3).map((weakness: WeaknessItem, index: number) => (
                  <li key={index} className="highlight-list__item">
                    {weakness.displayName}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDetailsTab = () => {
    if (!report) return null;

    return (
      <div className="report-tab report-tab--details">
        <h3 className="report-section__title">Detailed Feedback</h3>
        <div className="feedback-list">
          {report.scores.map((score: ScoreWithDetails) => {
            const feedbackItem = report.feedback[score.criterion] as FeedbackItem | undefined;
            const examples: FeedbackExample[] = score.examples || feedbackItem?.examples || [];
            const positives: string = score.positives || feedbackItem?.positives || '';
            
            return (
              <div key={score.criterion} className="feedback-card">
                <div className="feedback-card__header">
                  <h4 className="feedback-card__title">{score.displayName}</h4>
                  <div className="feedback-card__score">
                    <span 
                      className="feedback-card__score-value"
                      style={{ color: getScoreColor(score.value) }}
                    >
                      {score.value}
                    </span>
                    <span className="feedback-card__score-max">/10</span>
                  </div>
                </div>
                <div className="feedback-card__content">
                  {/* Summary */}
                  <p className="feedback-card__text">
                    {feedbackItem?.text || score.feedback || 'No detailed feedback available.'}
                  </p>
                  
                  {/* Positives */}
                  {positives && (
                    <div className="feedback-card__positives">
                      <span className="positives-label">✓ What you did well:</span>
                      <span className="positives-text">{positives}</span>
                    </div>
                  )}
                  
                  {/* Examples with corrections */}
                  {examples && examples.length > 0 && (
                    <div className="feedback-card__examples">
                      <h5 className="examples-title">📝 Specific Examples:</h5>
                      {examples.map((example: FeedbackExample, idx: number) => (
                        <div key={idx} className="example-item">
                          <div className="example-original">
                            <span className="example-label">Original:</span>
                            <span className="example-text example-text--original">"{example.original}"</span>
                          </div>
                          {example.corrected && example.corrected !== example.original && (
                            <div className="example-corrected">
                              <span className="example-label">Corrected:</span>
                              <span className="example-text example-text--corrected">"{example.corrected}"</span>
                            </div>
                          )}
                          {example.explanation && (
                            <div className="example-explanation">
                              <span className="explanation-text">💡 {example.explanation}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderImprovementTab = () => {
    if (!report) return null;

    return (
      <div className="report-tab report-tab--improvement">
        {/* Strengths Section */}
        <div className="report-section">
          <h3 className="report-section__title">
            <span className="section-icon section-icon--success">✓</span>
            Your Strengths
          </h3>
          <div className="strength-list">
            {report.strengths.map((strength: StrengthItem, index: number) => (
              <div key={index} className="strength-card">
                <div className="strength-card__header">
                  <h4 className="strength-card__title">{strength.displayName}</h4>
                  {strength.score !== undefined && (
                    <span className="strength-card__score">{strength.score}/10</span>
                  )}
                </div>
                <p className="strength-card__description">{strength.description}</p>
                {strength.example && (
                  <div className="strength-card__example">
                    <span className="example-label">📝 Example from your writing:</span>
                    <p className="example-quote">"{strength.example}"</p>
                  </div>
                )}
                {strength.isAboveAverage && (
                  <span className="strength-card__badge">Above Average</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Areas for Improvement Section */}
        <div className="report-section">
          <h3 className="report-section__title">
            <span className="section-icon section-icon--warning">↑</span>
            Areas for Improvement
          </h3>
          <div className="improvement-list">
            {report.areasForImprovement.map((weakness: WeaknessItem, index: number) => {
              const example: ImprovementExample | undefined = weakness.example;
              const priority: string | undefined = weakness.priority;
              
              return (
                <div key={index} className={`improvement-card ${priority === 'high' ? 'improvement-card--high-priority' : ''}`}>
                  <div className="improvement-card__header">
                    <h4 className="improvement-card__title">{weakness.displayName}</h4>
                    {priority && (
                      <span className={`improvement-card__priority priority--${priority}`}>
                        {priority === 'high' ? '🔴' : priority === 'medium' ? '🟡' : '🟢'} {priority} priority
                      </span>
                    )}
                  </div>
                  <p className="improvement-card__description">{weakness.description}</p>
                  
                  {/* Show example with correction */}
                  {example && example.original && (
                    <div className="improvement-card__example">
                      <h5 className="example-title">📝 Example from your writing:</h5>
                      <div className="example-comparison">
                        <div className="example-original">
                          <span className="example-label">❌ Original:</span>
                          <span className="example-text">"{example.original}"</span>
                        </div>
                        {example.corrected && (
                          <div className="example-corrected">
                            <span className="example-label">✓ Corrected:</span>
                            <span className="example-text">"{example.corrected}"</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {weakness.improvementTip && (
                    <div className="improvement-card__tip">
                      <span className="tip-label">💡 How to improve:</span>
                      <p className="tip-text">{weakness.improvementTip}</p>
                    </div>
                  )}
                  
                  {weakness.isBelowAverage && (
                    <span className="improvement-card__badge">Focus Area</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderExampleTab = () => {
    const filteredExamples = exampleWritings.filter(
      (ex) => ex.qualityLevel === selectedQuality
    );

    const getQualityLabel = (quality: string) => {
      switch (quality) {
        case 'high': return '⭐ Excellent';
        case 'medium': return '👍 Good';
        case 'low': return '📝 Developing';
        default: return quality;
      }
    };

    const getQualityDescription = (quality: string) => {
      switch (quality) {
        case 'high': return 'This is an example of excellent writing that demonstrates mastery of all criteria.';
        case 'medium': return 'This is an example of good writing that meets most expectations.';
        case 'low': return 'This is an example of developing writing that shows areas for improvement.';
        default: return '';
      }
    };

    return (
      <div className="report-tab report-tab--example">
        <div className="report-section">
          <h3 className="report-section__title">
            <span className="section-icon">📚</span>
            Example Writing for This Prompt
          </h3>
          <p className="example-intro">
            Compare your writing with these examples to understand what good writing looks like for this prompt.
          </p>

          {/* Quality Level Selector */}
          <div className="quality-selector">
            <span className="quality-selector__label">Select quality level:</span>
            <div className="quality-selector__buttons">
              {(['high', 'medium', 'low'] as const).map((quality) => (
                <button
                  key={quality}
                  className={`quality-btn ${selectedQuality === quality ? 'quality-btn--active' : ''} quality-btn--${quality}`}
                  onClick={() => setSelectedQuality(quality)}
                >
                  {getQualityLabel(quality)}
                </button>
              ))}
            </div>
          </div>

          {exampleLoading ? (
            <div className="example-loading">
              <div className="loading-spinner" />
              <p>Loading example writings...</p>
            </div>
          ) : filteredExamples.length > 0 ? (
            <div className="example-writings-list">
              {filteredExamples.map((example) => (
                <div key={example.id} className={`example-writing-card example-writing-card--${example.qualityLevel}`}>
                  <div className="example-writing-card__header">
                    <span className={`quality-badge quality-badge--${example.qualityLevel}`}>
                      {getQualityLabel(example.qualityLevel)}
                    </span>
                    <span className="example-writing-card__year">Year {example.yearLevel}</span>
                  </div>
                  
                  <p className="quality-description">{getQualityDescription(example.qualityLevel)}</p>
                  
                  <div className="example-writing-card__content">
                    <h4 className="content-title">Example Writing:</h4>
                    <div className="example-text-content">
                      {example.content.split('\n').map((paragraph, idx) => (
                        <p key={idx}>{paragraph}</p>
                      ))}
                    </div>
                  </div>

                  {example.explanation && (
                    <div className="example-writing-card__explanation">
                      <h4 className="explanation-title">💡 Why this is {example.qualityLevel === 'high' ? 'excellent' : example.qualityLevel === 'medium' ? 'good' : 'developing'}:</h4>
                      <p>{example.explanation}</p>
                    </div>
                  )}

                  {example.techniques && example.techniques.length > 0 && (
                    <div className="example-writing-card__techniques">
                      <h4 className="techniques-title">✨ Techniques Used:</h4>
                      <ul className="techniques-list">
                        {example.techniques.map((technique, idx) => (
                          <li key={idx} className="technique-item">{technique}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {example.associatedScores && example.associatedScores.length > 0 && (
                    <div className="example-writing-card__scores">
                      <h4 className="scores-title">📊 Scores:</h4>
                      <div className="scores-grid-mini">
                        {example.associatedScores.map((score, idx) => (
                          <div key={idx} className="score-mini">
                            <span className="score-mini__name">{score.criterion}</span>
                            <span className="score-mini__value">{score.value}/10</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="no-examples">
              <span className="no-examples__icon">📝</span>
              <p className="no-examples__text">
                No example writings available for this prompt at the {selectedQuality} quality level.
              </p>
              <p className="no-examples__hint">
                Try selecting a different quality level above.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="assessment-report assessment-report--loading">
        <div className="loading-spinner" />
        <p>Loading your assessment report...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="assessment-report assessment-report--error">
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button onClick={fetchReport} className="btn-retry">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="assessment-report assessment-report--empty">
        <p>No report available for this assessment.</p>
      </div>
    );
  }

  return (
    <div className="assessment-report">
      {/* Header */}
      <div className="report-header">
        <div className="report-header__left">
          {onBack && (
            <button onClick={onBack} className="btn-back" aria-label="Go back">
              ← Back
            </button>
          )}
          <div className="report-header__info">
            <h2 className="report-header__title">Assessment Report</h2>
            <div className="report-header__meta">
              <span className="report-badge">{report.testFormat.toUpperCase()}</span>
              <span className="report-badge">Year {report.yearLevel}</span>
              <span className="report-date">
                {new Date(report.generatedAt).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} className="btn-close" aria-label="Close report">
            ×
          </button>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="report-tabs">
        <button
          className={`report-tab-btn ${activeTab === 'overview' ? 'report-tab-btn--active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`report-tab-btn ${activeTab === 'details' ? 'report-tab-btn--active' : ''}`}
          onClick={() => setActiveTab('details')}
        >
          Detailed Feedback
        </button>
        <button
          className={`report-tab-btn ${activeTab === 'improvement' ? 'report-tab-btn--active' : ''}`}
          onClick={() => setActiveTab('improvement')}
        >
          Improvement Plan
        </button>
        <button
          className={`report-tab-btn ${activeTab === 'example' ? 'report-tab-btn--active' : ''}`}
          onClick={() => setActiveTab('example')}
        >
          Example Writing
        </button>
      </div>

      {/* Tab Content */}
      <div className="report-content">
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'details' && renderDetailsTab()}
        {activeTab === 'improvement' && renderImprovementTab()}
        {activeTab === 'example' && renderExampleTab()}
      </div>
    </div>
  );
};

export default AssessmentReport;
